import { createContext, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "../../utils/axios";
// settings
import actionTypes from "../../common/actionTypes";
import { DATABASE_STATUTES } from "../../common/constants";
import { APP_ROUTES_V2 } from "../../common/V2/constants";
// context
import useAuthContext from "../../hooks/useAuthContext";
// hooks
import useCustomReducer from "../../hooks/useCustomReducer";
import { customExperienceReducer } from "../../reducers/V2/customExperienceReducer";
// constants
import {
  SHIPPING_DATE_TYPES,
  BILLING_POLICY_TYPES,
  REMAINING_PAYMENT_TYPES,
  PRICING_POLICY_TYPES,
  MARKET_CONFIG_TYPES,
  LOCATION_CONFIG_TYPES,
} from "../../common/V2/customerExperienceConstants";
// utils
import shopifyAdminFetch from "../../utils/shopifyAdminFetch";
// GraphQL queries
import { getLocationsQuery } from "../../common/V2/shopify-graphql-queries";

// Endpoints
const CUSTOMER_EXPERIENCES_MARKETPLACE = "/v2/customer-experience/marketplace";
const CUSTOMER_EXPERIENCES_SHOP = "/v2/customer-experience";

const initialState = {
  marketplaceCustomerExperiences: [],
  marketplacePagination: {
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },
  shopCustomerExperiences: [],
  shopPagination: {
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },
  statusMarketplace: DATABASE_STATUTES.NOT_LOADED,
  statusShop: DATABASE_STATUTES.NOT_LOADED,
  errorMarketplace: null,
  errorShop: null,
  statusCustomerExperience: DATABASE_STATUTES.NOT_LOADED,
  customerExperience: null,
  errorCustomerExperience: null,
  locations: [],
  statusLocations: DATABASE_STATUTES.NOT_LOADED,
  errorLocations: null,
};

const CustomerExperiencesContext = createContext({
  ...initialState,
});

const CustomerExperiencesProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(customExperienceReducer, initialState);
  const { user } = useAuthContext();
  const { pathname } = useLocation();

  const fetchingIsActive =
    pathname.startsWith(APP_ROUTES_V2.CUSTOMER_EXPERIENCES.ROOT) || pathname.startsWith(APP_ROUTES_V2.PRODUCTS.ROOT);

  useEffect(() => {
    if (user?.domain && fetchingIsActive) {
      const loadData = async () => {
        try {
          // Load marketplace experiences only on customer experiences page
          if (pathname.startsWith(APP_ROUTES_V2.CUSTOMER_EXPERIENCES.ROOT)) {
            await Promise.all([loadMarketplaceCustomerExperiences(), loadShopCustomerExperiences()]);
          } else {
            // Load only shop experiences on products page
            await loadShopCustomerExperiences(/*page*/ 1, /* limit */ 100);
          }
        } catch (error) {
          dispatch({
            type: actionTypes.FETCH_MARKETPLACE_CUSTOMER_EXPERIENCES_FAILURE,
            error: error.message || "Failed to load customer experiences",
          });
        }
      };

      loadData();
    }
  }, [user?.domain, fetchingIsActive, pathname]);

  const loadMarketplaceCustomerExperiences = useCallback(
    async (page = 1, limit = 18, search, filters) => {
      try {
        dispatch({ type: actionTypes.FETCH_MARKETPLACE_CUSTOMER_EXPERIENCES });
        const params = {
          page,
          limit,
          sortBy: "createdAt",
          sortOrder: "DESC",
        };

        if (search && search.trim()) {
          params.search = search.trim();
        }

        if (filters && Object.keys(filters).length > 0) {
          params.filters = JSON.stringify(filters);
        }

        const response = await axios.get(CUSTOMER_EXPERIENCES_MARKETPLACE, { params });

        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.FETCH_MARKETPLACE_CUSTOMER_EXPERIENCES_SUCCESS,
            payload: {
              marketplaceCustomerExperiences: response.data.data.marketplaceCustomerExperiences || [],
              pagination: response.data.pagination || {},
            },
          });
        } else {
          throw new Error(response.data?.error || "Failed to load marketplace customer experiences");
        }
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_MARKETPLACE_CUSTOMER_EXPERIENCES_FAILURE,
          errorMarketplace: error.message || "Failed to load marketplace customer experiences",
        });
      }
    },
    [dispatch]
  );

  const loadShopCustomerExperiences = useCallback(
    async (page = 1, limit = 18, search, filters) => {
      try {
        dispatch({ type: actionTypes.FETCH_SHOP_CUSTOMER_EXPERIENCES });

        const params = {
          page,
          limit,
          sortBy: "createdAt",
          sortOrder: "DESC",
        };

        if (search && search.trim()) {
          params.search = search.trim();
        }

        if (filters && Object.keys(filters).length > 0) {
          params.filters = JSON.stringify(filters);
        }

        const response = await axios.get(CUSTOMER_EXPERIENCES_SHOP, { params });

        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.FETCH_SHOP_CUSTOMER_EXPERIENCES_SUCCESS,
            payload: {
              customerExperiences: response.data.data.customerExperiences || [],
              pagination: response.data.pagination || {},
            },
          });
        } else {
          throw new Error(response.data?.error || "Failed to load shop customer experiences");
        }
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_SHOP_CUSTOMER_EXPERIENCES_FAILURE,
          errorShop: error.message || "Failed to load shop customer experiences",
        });
      }
    },
    [dispatch]
  );

  const loadLocations = useCallback(async () => {
    try {
      dispatch({ type: actionTypes.FETCH_LOCATIONS });

      const { data } = await shopifyAdminFetch({ query: getLocationsQuery });

      if (data && data.locations && data.locations.edges) {
        const locations = data.locations.edges.map((edge) => ({
          id: edge.node.id.split("/").pop(), // Extract only the number from the end of the ID
          name: edge.node.name,
        }));

        dispatch({
          type: actionTypes.FETCH_LOCATIONS_SUCCESS,
          payload: locations,
        });

        return locations;
      } else {
        throw new Error("Invalid locations data structure");
      }
    } catch (error) {
      console.error("Error loading locations:", error);
      dispatch({
        type: actionTypes.FETCH_LOCATIONS_FAILURE,
        error: error.message || "Failed to load locations",
      });
      throw error;
    }
  }, [dispatch]);

  const createShopCustomerExperience = useCallback(
    async (title, overrides = {}) => {
      try {
        dispatch({ type: actionTypes.CREATE_SHOP_CUSTOMER_EXPERIENCE });

        const payload = {
          title,
          description: "",
          preOrderEnabled: false,
          backInStockEnabled: false,
          backInStockStoreLevelEnabled: false,
          comingSoonEnabled: false,
          lowStockEnabled: false,
          marketsConfigType: MARKET_CONFIG_TYPES.GLOBAL,
          inventoryLocationsConfigType: LOCATION_CONFIG_TYPES.ALL,
          ...overrides,
        };

        const response = await axios.post(CUSTOMER_EXPERIENCES_SHOP, payload);

        if (response.data && response.data.success) {
          const customerExperience = response.data.data.customerExperience;
          dispatch({
            type: actionTypes.CREATE_SHOP_CUSTOMER_EXPERIENCE_SUCCESS,
            payload: {
              customerExperience,
            },
          });
          return customerExperience;
        } else {
          throw new Error(response.data?.error || "Failed to create customer experience");
        }
      } catch (error) {
        dispatch({
          type: actionTypes.CREATE_SHOP_CUSTOMER_EXPERIENCE_FAILURE,
          error: error.message || "Failed to create customer experience",
        });
        throw error;
      }
    },
    [dispatch]
  );

  const duplicateMarketplaceCustomerExperience = useCallback(
    async (marketplaceCustomerExperienceId) => {
      try {
        dispatch({ type: actionTypes.DUPLICATE_MARKETPLACE_CUSTOMER_EXPERIENCE });

        const response = await axios.post(`${CUSTOMER_EXPERIENCES_MARKETPLACE}/duplicate`, null, {
          params: {
            marketplaceCustomerExperienceId,
          },
        });

        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.DUPLICATE_MARKETPLACE_CUSTOMER_EXPERIENCE_SUCCESS,
            payload: {
              customerExperience: response.data.data.customerExperience,
            },
          });
          return response.data.data.customerExperience;
        } else {
          throw new Error(response.data?.error || "Failed to duplicate customer experience");
        }
      } catch (error) {
        dispatch({
          type: actionTypes.DUPLICATE_MARKETPLACE_CUSTOMER_EXPERIENCE_FAILURE,
          error: error.message || "Failed to duplicate customer experience",
        });
        throw error;
      }
    },
    [dispatch]
  );

  const duplicateShopCustomerExperience = useCallback(
    async (customerExperienceId) => {
      try {
        dispatch({ type: actionTypes.DUPLICATE_SHOP_CUSTOMER_EXPERIENCE });

        const response = await axios.post(`${CUSTOMER_EXPERIENCES_SHOP}/${customerExperienceId}/duplicate`);

        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.DUPLICATE_SHOP_CUSTOMER_EXPERIENCE_SUCCESS,
            payload: {
              customerExperience: response.data.data.customerExperience,
            },
          });
          return response.data.data.customerExperience;
        } else {
          throw new Error(response.data?.error || "Failed to duplicate customer experience");
        }
      } catch (error) {
        dispatch({
          type: actionTypes.DUPLICATE_SHOP_CUSTOMER_EXPERIENCE_FAILURE,
          error: error.message || "Failed to duplicate customer experience",
        });
        throw error;
      }
    },
    [dispatch]
  );

  const deleteShopCustomerExperience = useCallback(
    async (customerExperienceId) => {
      try {
        dispatch({ type: actionTypes.DELETE_SHOP_CUSTOMER_EXPERIENCE });

        const response = await axios.delete(`${CUSTOMER_EXPERIENCES_SHOP}/${customerExperienceId}`);

        // Handle 204 No Content response (successful delete)
        if (response.status === 204) {
          dispatch({
            type: actionTypes.DELETE_SHOP_CUSTOMER_EXPERIENCE_SUCCESS,
            payload: {
              customerExperienceId,
            },
          });
          return true;
        } else {
          throw new Error("Failed to delete customer experience");
        }
      } catch (error) {
        dispatch({
          type: actionTypes.DELETE_SHOP_CUSTOMER_EXPERIENCE_FAILURE,
          error: error.message || "Failed to delete customer experience",
        });
        throw error;
      }
    },
    [dispatch]
  );

  const loadCustomerExperience = useCallback(
    async (customerExperienceId) => {
      try {
        dispatch({ type: actionTypes.FETCH_CUSTOMER_EXPERIENCE });

        const response = await axios.get(`${CUSTOMER_EXPERIENCES_SHOP}/${customerExperienceId}`);

        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.FETCH_CUSTOMER_EXPERIENCE_SUCCESS,
            payload: response.data.data,
          });
          return response.data.data;
        } else {
          throw new Error(response.data?.error || "Failed to load customer experience");
        }
      } catch (error) {
        console.error("Error loading customer experience:", error);
        dispatch({
          type: actionTypes.FETCH_CUSTOMER_EXPERIENCE_FAILURE,
          error: error.message || "Failed to load customer experience",
        });
        throw error;
      }
    },
    [dispatch]
  );

  const loadMarketplaceCustomerExperience = useCallback(
    async (marketplaceCustomerExperienceId) => {
      try {
        dispatch({ type: actionTypes.FETCH_CUSTOMER_EXPERIENCE });

        const response = await axios.get(`${CUSTOMER_EXPERIENCES_MARKETPLACE}/${marketplaceCustomerExperienceId}`);
        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.FETCH_CUSTOMER_EXPERIENCE_SUCCESS,
            payload: response.data.data,
          });
          return response.data.data;
        } else {
          throw new Error(response.data?.error || "Failed to load customer experience");
        }
      } catch (error) {
        console.error("Error loading marketplace customer experience:", error);
        dispatch({
          type: actionTypes.FETCH_CUSTOMER_EXPERIENCE_FAILURE,
          error: error.message || "Failed to load marketplace customer experience",
        });
        throw error;
      }
    },
    [dispatch]
  );

  const prepareCustomerExperienceForUpdate = useCallback((customerExperience, overrides = {}) => {
    if (!customerExperience) {
      throw new Error("Customer experience data is required");
    }

    // Apply overrides to customer experience data
    const mergedData = { ...customerExperience, ...overrides };

    // Helper function to clean preOrderConfig based on schema rules
    const cleanPreOrderConfig = (config) => {
      if (!config) return null;

      const cleaned = {
        stockOption: config.stockOption,
        shippingDateType: config.shippingDateType,
        billingPolicyType: config.billingPolicyType,
        inventoryPolicy: config.inventoryPolicy,
        uiConfig: config.uiConfig || {},
      };

      // Only include shippingDateJsonb if NOT ASAP
      if (config.shippingDateType !== SHIPPING_DATE_TYPES.ASAP && config.shippingDateJsonb) {
        cleaned.shippingDateJsonb = {};

        // Include appropriate fields based on shippingDateType
        if (config.shippingDateType === SHIPPING_DATE_TYPES.EXACT_DATE && config.shippingDateJsonb.specificDate) {
          cleaned.shippingDateJsonb.specificDate = config.shippingDateJsonb.specificDate;
        }

        if (config.shippingDateType === SHIPPING_DATE_TYPES.PERIOD) {
          if (config.shippingDateJsonb.periodValue) {
            cleaned.shippingDateJsonb.periodValue = parseInt(config.shippingDateJsonb.periodValue, 10);
          }
          if (config.shippingDateJsonb.periodType) {
            cleaned.shippingDateJsonb.periodType = config.shippingDateJsonb.periodType;
          }
        }

        if (config.shippingDateType === SHIPPING_DATE_TYPES.INTERVAL) {
          if (config.shippingDateJsonb.periodStart) {
            cleaned.shippingDateJsonb.periodStart = {
              value: parseInt(config.shippingDateJsonb.periodStart.value, 10),
              type: config.shippingDateJsonb.periodStart.type,
            };
          }
          if (config.shippingDateJsonb.periodEnd) {
            cleaned.shippingDateJsonb.periodEnd = {
              value: parseInt(config.shippingDateJsonb.periodEnd.value, 10),
              type: config.shippingDateJsonb.periodEnd.type,
            };
          }
        }
      }

      // Only include billingPolicyJsonb if PARTIAL_PAYMENT
      if (config.billingPolicyType === BILLING_POLICY_TYPES.PARTIAL_PAYMENT && config.billingPolicyJsonb) {
        cleaned.billingPolicyJsonb = {
          upfrontPayment: {
            type: config.billingPolicyJsonb.upfrontPayment.type,
            value: Number(config.billingPolicyJsonb.upfrontPayment.value),
          },
          remainingPaymentType: config.billingPolicyJsonb.remainingPaymentType,
        };

        // Only include the appropriate payment field based on remainingPaymentType
        if (config.billingPolicyJsonb.remainingPaymentType === REMAINING_PAYMENT_TYPES.EXACT_DATE) {
          cleaned.billingPolicyJsonb.remainingPaymentDate = config.billingPolicyJsonb.remainingPaymentDate;
        } else if (config.billingPolicyJsonb.remainingPaymentType === REMAINING_PAYMENT_TYPES.TIME_AFTER_CHECKOUT) {
          cleaned.billingPolicyJsonb.timeAfterCheckout = {
            value: parseInt(config.billingPolicyJsonb.timeAfterCheckout.value, 10),
            unit: config.billingPolicyJsonb.timeAfterCheckout.unit,
          };
        }

        // Include description if provided
        if (config.billingPolicyJsonb.description) {
          cleaned.billingPolicyJsonb.description = config.billingPolicyJsonb.description;
        }
      }

      // Handle pricing policy
      if (config.pricingPolicyType && config.pricingPolicyType !== PRICING_POLICY_TYPES.NO_DISCOUNT) {
        cleaned.pricingPolicyType = config.pricingPolicyType;
        if (config.pricingPolicyValue !== null && config.pricingPolicyValue !== undefined) {
          // Convert to number for schema compliance
          cleaned.pricingPolicyValue = Number(config.pricingPolicyValue);
        }
      } else {
        cleaned.pricingPolicyType = PRICING_POLICY_TYPES.NO_DISCOUNT;
        // Don't include pricingPolicyValue for NO_DISCOUNT per schema
      }

      return cleaned;
    };

    // Helper function to clean backInStockConfig based on schema rules
    const cleanBackInStockConfig = (config) => {
      if (!config) return null;

      const cleaned = {
        stockOption: config.stockOption,
        uiConfig: config.uiConfig || {},
        quantityThreshold: parseInt(config.quantityThreshold, 10),
      };

      return cleaned;
    };

    // Helper function to clean comingSoonConfig based on schema rules
    const cleanComingSoonConfig = (config) => {
      if (!config) return null;

      const cleaned = {
        stockOption: config.stockOption,
        uiConfig: config.uiConfig || {},
      };

      return cleaned;
    };

    // Helper function to clean lowStockConfig based on schema rules
    const cleanLowStockConfig = (config) => {
      if (!config) return null;

      const cleaned = {
        stockOption: config.stockOption,
        quantityThreshold: parseInt(config.quantityThreshold, 10),
        uiConfig: config.uiConfig || {},
      };

      return cleaned;
    };

    // Start with base updatable fields (all required by schema)
    const updateData = {
      // Required basic fields
      title: mergedData.title,
      description: mergedData.description || "", // Default empty string if null

      // Required feature flags
      preOrderEnabled: mergedData.preOrderEnabled,
      backInStockEnabled: mergedData.backInStockEnabled,
      backInStockStoreLevelEnabled: mergedData.backInStockStoreLevelEnabled || false,
      comingSoonEnabled: mergedData.comingSoonEnabled,
      lowStockEnabled: mergedData.lowStockEnabled,

      // Required markets and locations configuration
      marketsConfigType: mergedData.marketsConfigType,
      inventoryLocationsConfigType: mergedData.inventoryLocationsConfigType,
    };

    // Add optional icon field if it exists
    if (mergedData.icon) {
      updateData.icon = mergedData.icon;
    }

    // Add config objects based on feature flags (conditional validation with proper cleaning)
    // Send config if feature is enabled OR if it has existing config (to preserve it)
    if (mergedData.preOrderConfig) {
      updateData.preOrderConfig = cleanPreOrderConfig(mergedData.preOrderConfig);
    }

    if (mergedData.backInStockConfig) {
      updateData.backInStockConfig = cleanBackInStockConfig(mergedData.backInStockConfig);
    }

    if (mergedData.comingSoonConfig) {
      updateData.comingSoonConfig = cleanComingSoonConfig(mergedData.comingSoonConfig);
    }

    if (mergedData.lowStockConfig) {
      updateData.lowStockConfig = cleanLowStockConfig(mergedData.lowStockConfig);
    }

    // Add conditional arrays based on configuration type (conditional validation)
    if (
      mergedData.marketsConfigType === MARKET_CONFIG_TYPES.ISO_CODE ||
      mergedData.marketsConfigType === MARKET_CONFIG_TYPES.SHOPIFY_MARKET
    ) {
      if (mergedData.markets && mergedData.markets.length > 0) {
        updateData.markets = mergedData.markets;
      } else {
        // Schema requires markets array when not GLOBAL, but it can be empty
        updateData.markets = [];
      }
    }

    if (mergedData.inventoryLocationsConfigType === LOCATION_CONFIG_TYPES.SPECIFIC) {
      if (mergedData.inventoryLocations && mergedData.inventoryLocations.length > 0) {
        updateData.inventoryLocations = mergedData.inventoryLocations;
      } else {
        // Schema requires inventoryLocations array when SPECIFIC, but it can be empty
        updateData.inventoryLocations = [];
      }
    }

    return updateData;
  }, []);

  const updateCustomerExperience = useCallback(
    async (customerExperienceId, updateData) => {
      try {
        console.log("Updating customer experience:", customerExperienceId, updateData);

        dispatch({ type: actionTypes.UPDATE_CUSTOMER_EXPERIENCE });

        const response = await axios.put(`${CUSTOMER_EXPERIENCES_SHOP}/${customerExperienceId}`, updateData);

        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.UPDATE_CUSTOMER_EXPERIENCE_SUCCESS,
            payload: response.data.data,
          });
          return response.data.data;
        } else {
          throw new Error(response.data?.error || "Failed to update customer experience");
        }
      } catch (error) {
        console.error("Error updating customer experience:", error);
        dispatch({
          type: actionTypes.UPDATE_CUSTOMER_EXPERIENCE_FAILURE,
          error: error.message || "Failed to update customer experience",
        });
        throw error;
      }
    },
    [dispatch]
  );

  const memoizedState = useMemo(
    () => ({
      ...state,
      loadMarketplaceCustomerExperiences,
      loadShopCustomerExperiences,
      loadLocations,
      duplicateMarketplaceCustomerExperience,
      duplicateShopCustomerExperience,
      deleteShopCustomerExperience,
      loadCustomerExperience,
      loadMarketplaceCustomerExperience,
      updateCustomerExperience,
      prepareCustomerExperienceForUpdate,
      createShopCustomerExperience,
    }),
    [
      state,
      loadMarketplaceCustomerExperiences,
      loadShopCustomerExperiences,
      loadLocations,
      duplicateMarketplaceCustomerExperience,
      duplicateShopCustomerExperience,
      deleteShopCustomerExperience,
      loadCustomerExperience,
      loadMarketplaceCustomerExperience,
      updateCustomerExperience,
      prepareCustomerExperienceForUpdate,
      createShopCustomerExperience,
    ]
  );

  return <CustomerExperiencesContext.Provider value={memoizedState}>{children}</CustomerExperiencesContext.Provider>;
};

export { CustomerExperiencesContext, CustomerExperiencesProvider };
